import * as angular from 'angular';
import * as moment from 'moment';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { TimeZoneHelper } from '../../../helpers/helpers';
import { getTimeZone } from '../../../../common/getTimezone';

export interface IDateTimePickerScope extends angular.IScope {
    vm: DateTimePicker;
    directivevm: any;
    selection: any;
}

export interface IDateTimePicker {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;

    offset: number;

    date: string;
    actualDate(): moment.Moment;
    selectedDate: string;
    placeholder: string;

    isCalendarVisible: boolean;

    monthName(): string;
    toggleVisibility(): void;
    nextMonth(): void;
    previousMonth(): void;
    nextHour(): void;
    previousHour(): void;
    nextMinute(): void;
    previousMinute(): void;
    setNow(): void;
    setDate(): void;
    cancel(): void;

    setProperties(dvm);
}

export class DateTimePicker implements IDateTimePicker {
    static controllerId = 'DateTimePicker';

    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    offset: number;
    placeholder: string;
    title: string;

    selectedYear: number;
    selectedMonth: number;

    date: string;
    selectedDate: string;
    name: string;

    isCalendarVisible = false;

    change: any;

    private _format = 'YYYY-MM-DD HH:mm';

    // private _selection;
    private _hourNeedle;
    private _minuteNeedle;
    private _size;
    selection;

    //new
    selectedMoment: moment.Moment; //in drop-down
    actualMoment: moment.Moment; //to be bound to
    displayDate: string; //in input

    static $inject = ['$scope'];

    constructor(private $scope: IDateTimePickerScope) {
        const tzh = new TimeZoneHelper();
        this.offset = tzh.getOffset(new Date(), getTimeZone());
        const today = moment();
        this.year = today.year();
        this.month = today.month();
        this.day = today.date();
        this.selectedYear = this.year;
        this.selectedMonth = this.month;
        this.isCalendarVisible = false;
        const self = this;
        $scope.$watchGroup(
            [
                () => self.day,
                () => self.hour,
                () => self.minute
            ], () => {
                self.updateDate(self);
            });
        $scope.$watch(() => self.month, () => {
            self.drawCalendar(self.year, self.month, $scope);
        });
        $scope.$watch(() => self.date, () => {
            let selectedMoment = self.actualDate();
            if (!selectedMoment) {
                selectedMoment = moment(self.title);
            }
            self.selectedDate = selectedMoment.format(this._format);
            self.year = selectedMoment.year();
            self.month = selectedMoment.month();
            self.selectedYear = self.year;
            self.selectedMonth = self.month;
            self.hour = selectedMoment.hour();
            self.minute = selectedMoment.minute();
            self.day = selectedMoment.date();
        });
    }

    setProperties(dvm) {
        this._size = dvm.size;
        this.selection = dvm.selection;
    }

    private updateDate(self: DateTimePicker) {
        if (this.selectedYear && (this.selectedMonth || this.selectedMonth === 0)) {
            const selectedMoment = moment()
                .year(self.selectedYear)
                .month(self.selectedMonth)
                .date(self.day)
                .hours(self.hour ? self.hour : 0)
                .minutes(self.minute ? self.minute : 0)
                .seconds(0)
                .milliseconds(0);
            this.selectedDate = selectedMoment.clone().format(this._format);
        }
    }

    actualDate(): moment.Moment {
        let result: moment.Moment;
        if (this.date === '*') {
            result = moment();
        } else {
            result = moment(this.date, this._format);
        }
        if (!this.title) {
            // TODO ISO realy ? format ?
            this.title = result.toISOString();
        }
        return result;
    }

    monthName(): string {
        return moment().month(this.month).format('MMMM');
    }

    toggleVisibility(): void {
        if (!this.isCalendarVisible) {
            const selected = moment(this.title);
            if (selected) {
                this.drawCalendar(selected.year(), selected.month(), this.$scope);
            }
        }
        this.isCalendarVisible = !this.isCalendarVisible;
    }

    nextMonth(): void {
        this.month++;
        if (this.month > 11) {
            this.month = 0;
            this.year++;
        }
    }

    previousMonth(): void {
        this.month--;
        if (this.month < 0) {
            this.month = 11;
            this.year--;
        }
    }

    nextHour(): void {
        this.hour++;
        if (this.hour > 23) {
            this.hour = 0;
        }
    }

    previousHour(): void {
        this.hour--;
        if (this.hour < 0) {
            this.hour = 23;
        }
    }

    nextMinute(): void {
        this.minute++;
        if (this.minute > 59) {
            this.minute = 0;
        }
    }

    previousMinute(): void {
        this.minute--;
        if (this.minute < 0) {
            this.minute = 59;
        }
    }

    setNow(): void {
        this.selectedDate = '*';
    }

    setToday(): void {
        const today = moment();
        today.minute(this.minute);
        today.hour(this.hour);
        this.selectedDate = today.format(this._format);
        this.setDate();
    }

    setDate(): void {
        this.date = this.selectedDate;
        this.toggleVisibility();
    }

    cancel(): void {
        this.selectedDate = moment().format(this._format);
        this.toggleVisibility();
    }

    //TO be moved to directive
    private drawCalendar(year: number, monthNumber: number, scope: IDateTimePickerScope) {
        const days = [];
        const month = moment().month(monthNumber);
        const daysInMonth = month.daysInMonth();
        const firstDayOfMonth = moment(new Date(year, monthNumber, 1));
        const firstDay = firstDayOfMonth.weekday();
        const m = firstDayOfMonth.clone().date(0);
        for (let i = 0; i < daysInMonth; i++) {
            m.add(1, 'd');
            const day = {
                monthWeek: Math.floor((i + firstDay) / 7),
                day: i + 1,
                week: m.week(),
                moment: m.clone()
            };
            days.push(day);
        }

        const minMonthWeek = <number>d3.min(days, (d) => { return d.monthWeek; });
        const s = this.selection.select('.dates')
            .selectAll('.date');
        const selectionData = s.data(days);
        const selectionEnter = selectionData.enter();
        const g = selectionEnter.append('g')
            .classed('date', true)
            .on('click', (d) => {
                // grep current hour
                d.moment.hour(this.hour);
                d.moment.minute(this.minute);
                this.day = d.day;
                this.selectedMonth = this.month;
                this.selectedYear = this.year;
                this.selection.select('.dates')
                    .selectAll('.date')
                    .classed('selected', (innerD) => {
                        return this.highlight(innerD);
                    });
                this.selectedDate = d.moment.format(this._format);
                this.setDate();
                if (this.change) {
                    this.change({ date: this.selectedDate });
                }
                scope.$apply();
            });
        selectionData
            .attr({
                'transform': d => {
                    return `translate(${d.moment.weekday() * this._size},${(d.monthWeek - minMonthWeek) * this._size})`;
                }
            })
            .classed('today', d => this.isToday(d.moment))
            .classed('selected', d => this.highlight(d));
        g.append('rect')
            .attr({
                x: 2,
                y: 2,
                width: this._size - 4,
                height: this._size - 4
            });
        g.append('text')
            .attr({
                'x': this._size / 2,
                'y': this._size / 2,
                'dy': '.35em',
                width: this._size,
                height: this._size
            })
            .text(d => d.day);
        selectionData.exit().remove();
        const wy = _.map(days, 'week');
        const weekNumbers = _.uniq(wy);
        const ds = this.selection
            .select('.weekNumbers')
            .selectAll('text')
            .data(weekNumbers);
        ds.enter().append('text');
        ds.attr({
            'transform': (_d, i) => `translate(${0},${(i + 1) * this._size})`
        })
            .text(d => d);
        ds.exit().remove();

    }

    private highlight(d): boolean {
        return d.moment.year() === this.selectedYear
            && d.moment.month() === this.selectedMonth
            && d.moment.date() === this.day;
    }

    private isToday(day: moment.Moment): boolean {
        const today = moment();
        const currentYear = today.year();
        const currentMonth = today.month();
        const currenDay = today.date();
        let result = false;
        if (currentYear === day.year() && currentMonth === day.month() && currenDay === day.date()) {
            result = true;
        }
        return result;
    }

    private setNeedles(hour, minute) {
        const total = 12 * 60;
        const current = hour * 60 + minute;
        const percent = current / total;
        this._hourNeedle
            .transition()
            .attr({
                'transform': `rotate(${percent * 360})`
            });
        this._minuteNeedle
            .transition()
            .attr({
                'transform': `rotate(${minute / 60 * 360})`
            });
    }

    adjustSize() {
        const width = $(this.selection[0][0]).width();
    }
}